import { Link, Meta, MetaProvider } from "@solidjs/meta";
import { type RouteSectionProps, useLocation } from "@solidjs/router";
import { clientOnly } from "@solidjs/start";
import { ErrorBoundary, Suspense } from "solid-js";
import { SolidNProgress } from "solid-progressbar";

import "~/code-highlight.scss";
import "~/pollen.css";
import BackToTop from "~/components/BackToTop";
import Footer from "~/components/Footer";
import Header from "~/components/Header";
import PlayerContext from "~/components/PlayerContext";
import "~/components/Root.module.scss";
import HandleError from "~/components/HandleError";
import canonicalUrl from "~/utils/canonicalUrl";

const Player = clientOnly(() => import("~/components/player"));
const SongLyricsModal = clientOnly(
  () => import("~/components/player/SongLyricsModal"),
);
const SongRequestModal = clientOnly(
  () => import("~/components/SongRequestModal"),
);

export default function Root(props: RouteSectionProps) {
  return (
    <MetaProvider>
      <DefaultHead />
      <PlayerContext>
        <Suspense>
          <Header />
          <ErrorBoundary
            fallback={(error, reset) => (
              <HandleError error={error} reset={reset} />
            )}
          >
            <Suspense>{props.children}</Suspense>
          </ErrorBoundary>
          <SolidNProgress
            color="light-dark(var(--color-sky-400), var(--color-sky-600))"
            stopDelayMs={0}
            options={{ showSpinner: false }}
          />
          <Footer />
          <BackToTop />
          <Player />
          <SongLyricsModal />
          <SongRequestModal />
        </Suspense>
      </PlayerContext>
    </MetaProvider>
  );
}

function DefaultHead() {
  const location = useLocation();
  const canonical = canonicalUrl(location.pathname, location.search);

  return (
    <>
      <Link rel="canonical" href={canonical} />
      <Meta property="og:url" content={canonical} />
      <Meta property="og:site_name" content="Rádio J-Hero" />
      <Meta property="og:locale" content="pt-BR" />
      <Meta name="twitter:card" content="summary" />
      <Meta name="twitter:site" content="@RadioJHero" />
    </>
  );
}
